import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import StudentsFilterBar from './StudentsFilterBar';
import StudentsTable from './StudentsTable';
import StudentDetailModal from './StudentDetailModal';
import StudentEditModal from './StudentEditModal';
import { useCan } from '@/auth/AuthContext';

// Пагинацию показываем только если учеников > этого порога. До 500 рендерим всех одной страницей.
const PAGE_THRESHOLD = 500;
const PER_PAGE = 500;

// Схема URL-состояния. Дефолты в URL не пишутся.
//   q           — поисковая строка
//   status      — фильтр по статусу (active|archive|all). По умолчанию — active,
//                 чтобы не путаться с архивными в основном рабочем потоке.
//   sort        — ключ сортировки (name|class|birthdate|status)
//   dir         — направление (asc|desc)
//   page        — номер страницы
//   student     — id ученика для открытой модалки, или 'new' для создания
//   studentEdit — '1', если поверх детали открыта форма редактирования
const URL_SCHEMA = {
  q: '',
  status: 'active',
  sort: 'name',
  dir: 'asc',
  page: 1,
  student: null,
  studentEdit: null,
};

export default function StudentsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const [params, setParams] = useUrlState(URL_SCHEMA);
  const canWrite = can('students', 'write');

  // Триггер «обновить таблицу» — увеличиваем при сохранении ученика, useEffect перетянет данные
  const [reloadTick, setReloadTick] = useState(0);
  const triggerReload = () => setReloadTick((n) => n + 1);

  // Локальное значение в инпуте поиска (мгновенное), отдельно от URL — чтобы
  // не дёргать URL на каждый символ. Синхронизируем при внешних изменениях.
  const [searchInput, setSearchInput] = useState(params.q);
  useEffect(() => { setSearchInput(params.q); }, [params.q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== params.q) setParams({ q: searchInput, page: 1 });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({
      search: params.q,
      status: params.status,
      sort: params.sort,
      dir: params.dir,
      page: String(params.page),
      per_page: String(PER_PAGE),
    });
    api.get(`/api/students?${qs.toString()}`)
      .then((data) => {
        if (aborted) return;
        // Страховка от дублей по id: одинаковый React key на нескольких строках
        // ломает reconciliation и оставляет «призрачные» строки в DOM. Бэкенд
        // дублей отдавать не должен, но подстрахуемся на уровне рендера.
        const seen = new Set();
        const unique = (data.rows || []).filter((r) => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
        setRows(unique);
        setTotal(data.total);
      })
      .catch((e) => { if (!aborted) setError(e.message); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [params.q, params.status, params.sort, params.dir, params.page, reloadTick]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => {
    if (rows.every((r) => prev.has(r.id))) return new Set();
    return new Set(rows.map((r) => r.id));
  });
  const setSortKey = (key) => {
    if (params.sort === key) {
      setParams({ dir: params.dir === 'asc' ? 'desc' : 'asc', page: 1 });
    } else {
      setParams({ sort: key, dir: 'asc', page: 1 });
    }
  };

  const showPagination = total > PAGE_THRESHOLD;
  const totalPages = showPagination ? Math.max(1, Math.ceil(total / PER_PAGE)) : 1;

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Ученики']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <StudentsFilterBar
        filters={{ status: params.status }}
        setFilters={(f) => setParams({ status: f.status, page: 1 })}
        search={searchInput}
        setSearch={setSearchInput}
        totalShown={rows.length}
        total={total}
        onClearAll={() => { setParams({ status: 'active', q: '', page: 1 }); setSearchInput(''); }}
        canCreate={canWrite}
        onCreate={() => setParams({ student: 'new' })}
      />
      <div className="main-content-wrap">
        {error && (
          <div style={{ padding: 16, color: 'var(--red-fg)', background: 'var(--red-bg)', borderRadius: 8, margin: 16 }}>
            Ошибка: {error}
          </div>
        )}
        <StudentsTable
          rows={rows}
          selected={selected}
          toggle={toggle}
          toggleAll={toggleAll}
          allSelected={rows.length > 0 && rows.every((r) => selected.has(r.id))}
          showAvatars={true}
          sort={{ key: params.sort, dir: params.dir }}
          setSort={setSortKey}
          onOpen={(id) => setParams({ student: id })}
        />
        <div className="table-foot">
          <span>
            {loading
              ? 'Загрузка…'
              : <>Строк: <b style={{ color: 'var(--fg)' }}>{rows.length}</b> из {total}</>}
          </span>
          {showPagination && totalPages > 1 && (
            <div className="table-foot__pages">
              <button className="pagebtn" disabled={params.page === 1} onClick={() => setParams({ page: params.page - 1 })}>‹</button>
              <span style={{ padding: '0 8px' }}>{params.page} / {totalPages}</span>
              <button className="pagebtn" disabled={params.page >= totalPages} onClick={() => setParams({ page: params.page + 1 })}>›</button>
            </div>
          )}
        </div>
      </div>

      {params.student === 'new' && (
        <StudentEditModal
          mode="create"
          onClose={() => setParams({ student: null })}
          onSaved={(s) => { triggerReload(); setParams({ student: String(s.id) }); }}
        />
      )}

      {params.student && params.student !== 'new' && !params.studentEdit && (
        <StudentDetailModal
          studentId={Number(params.student)}
          canEdit={canWrite}
          onEdit={() => setParams({ studentEdit: '1' })}
          onClose={() => setParams({ student: null })}
        />
      )}

      {params.student && params.student !== 'new' && params.studentEdit && (
        <StudentEditModal
          mode="edit"
          studentId={Number(params.student)}
          onClose={() => setParams({ studentEdit: null })}
          onSaved={() => { triggerReload(); setParams({ studentEdit: null }); }}
          onDeleted={() => { triggerReload(); setParams({ student: null, studentEdit: null }); }}
        />
      )}
    </>
  );
}
